import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";

import { makeStyles } from "@mui/styles";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper, PermissionTooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Gem } from "lucide-react";
import { openDownloadLinkCallback } from "utils/download";
import { hasPremiumData } from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { getStatusColor } from "../../../clip-utils";
import { MapThumbnail } from "./map-thumbnail";

interface ClipItemProps {
  clip: ClipConfiguration;
  onOpen: (clip: ClipConfiguration) => void;
  onSelect: (clip: ClipConfiguration) => void;
  isSelected: boolean;
}

export const ClipItem = (props: ClipItemProps) => {
  const theme = useTheme();

  const { isPermitted, permissionDeniedMessageString, isPermissionsSuccess } =
    usePermissions();

  const clipPermissions = [
    PermissionsKeys.clipCreate,
    PermissionsKeys.clipUpdate,
    PermissionsKeys.clipRead,
  ];

  const classes = useStyles();
  const geometry =
    props.clip.geometry && props.clip.geometry.features.length > 0
      ? props.clip.geometry
      : undefined;

  const premiumClip = hasPremiumData(props.clip);

  // Allow opening all clips regardless of premium data - subscription check happens at submission
  const isClipPermitted = isPermissionsSuccess && isPermitted(clipPermissions);
  const permissionDeniedString = !isClipPermitted
    ? permissionDeniedMessageString(clipPermissions)
    : "";

  dayjs.extend(utc);

  // There are two columns for Thumbnail + Info. The info is organised by row so chips can
  //  independetly truncate text on their row if needed
  return (
    <Paper
      variant="outlined"
      className={props.isSelected ? classes.itemSelected : classes.item}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onSelect(props.clip);
      }}
    >
      <Box>
        <MapThumbnail geometry={geometry} />
      </Box>

      <Box sx={{ width: "226px", padding: 1 }}>
        <Stack
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip title={props.clip.name}>
              <Typography variant="subtitle1" noWrap>
                {props.clip.name}
              </Typography>
            </Tooltip>
            <Chip
              color={getStatusColor(props.clip.status)}
              size="small"
              label={props.clip.status}
              variant="outlined"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <AccountBoxIcon color="action"></AccountBoxIcon>
              <Typography
                variant="body2"
                color={theme.palette.text.secondary}
                noWrap
                sx={{ minWidth: 0 }}
              >
                {props.clip.authorName || props.clip.developer || ""}
              </Typography>
            </Box>
            {premiumClip && <Gem color="#1876d3" size={20} />}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarTodayIcon color="action"></CalendarTodayIcon>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                {dayjs.utc(props.clip.date).local().format("YYYY-MM-DD")}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {props.clip.status === "Completed" && premiumClip && (
                <PermissionTooltipWrapper
                  permissionKeys={[PermissionsKeys.premiumDataRead]}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={openDownloadLinkCallback(
                      `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                        props.clip.id
                      }/output`
                    )}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </PermissionTooltipWrapper>
              )}
              {props.clip.status === "Completed" && !premiumClip && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={openDownloadLinkCallback(
                    `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                      props.clip.id
                    }/output`
                  )}
                >
                  <FileDownloadIcon />
                </IconButton>
              )}
              <TooltipWrapper title={permissionDeniedString}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => props.onOpen(props.clip)}
                  disabled={!isClipPermitted}
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    paddingLeft: "5px",
                    paddingRight: "5px",
                    paddingTop: "2px",
                  }}
                >
                  Open
                </Button>
              </TooltipWrapper>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  photo: {
    width: "100px",
    height: "100px",
    textAlign: "center",
    alignContent: "center",
    backgroundColor: theme.palette.action.hover,
  },
  item: {
    minHeight: "100px",
    display: "flex",
    flexDirection: "row",
    gap: 1,
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  itemSelected: {
    minHeight: "100px",
    display: "flex",
    flexDirection: "row",
    gap: 1,
    background: theme.palette.action.selected,
  },
}));
